// @ts-nocheck
import { Calendar, MapPin, Image } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

interface UpcomingEventsProps {
  sportSlug: string;
  sportName?: string;
  gender?: "Men" | "Women";
  categoryId?: string;
}

export function UpcomingEvents({ sportSlug, sportName = "basketball", gender, categoryId }: UpcomingEventsProps) {
  const { data: events, isLoading, error } = useUpcomingEvents({ sportSlug, gender, categoryId });

  // Helper function to get the full event image URL
  const getEventImageUrl = (thumbnailUrl: string | null | undefined) => {
    if (!thumbnailUrl) return null;
    
    // If it's already a full URL, return as is
    if (thumbnailUrl.startsWith('http://') || thumbnailUrl.startsWith('https://')) {
      return thumbnailUrl;
    }
    
    // Otherwise, build the Supabase storage URL
    const { data } = supabase.storage.from('event-featured').getPublicUrl(thumbnailUrl);
    return data?.publicUrl || null;
  };

  if (isLoading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-secondary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading upcoming events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <Calendar className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-2xl font-black text-red-600 mb-2">Error Loading Events</h3>
        <p className="text-red-500">Unable to load events at this time. Please try again later.</p>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-16">
        <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-2xl font-black text-gray-600 mb-2">No Events Currently Scheduled</h3>
        <p className="text-gray-500">Check back soon for upcoming {sportName} tournaments, camps, and showcases.</p>
      </div>
    );
  }

  // Parse date strings as local dates (not UTC) to avoid timezone issues
  // Database stores dates as "YYYY-MM-DD" which JavaScript parses as UTC midnight
  // This causes dates to appear as the previous day in US timezones
  const parseLocalDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const formatEventDate = (startDate: string, endDate: string | null) => {
    const start = parseLocalDate(startDate);
    const end = endDate ? parseLocalDate(endDate) : null;
    
    if (end && start.toDateString() !== end.toDateString()) {
      return `${format(start, "MMM dd")} - ${format(end, "MMM dd, yyyy")}`;
    }
    return format(start, "MMM dd, yyyy");
  };

  const formatLocation = (city: string | null, state: string | null) => {
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return "Location TBA";
  };

  const isRegistrationOpen = (openDate: string | null, closeDate: string | null) => {
    if (!openDate || !closeDate) return false;
    const now = new Date();
    const open = new Date(openDate);
    const close = new Date(closeDate);
    return now >= open && now <= close;
  };

  return (
    <div className="flex flex-col items-center">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
        {events.map((event) => {
          const imageUrl = getEventImageUrl(event.thumbnail_url);
          
          return (
            <Card key={event.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300 border border-gray-200 bg-white">
              {/* Image section with badges */}
              <div className="relative h-48 overflow-hidden bg-gray-100">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const placeholder = parent.querySelector('.placeholder-icon');
                        if (placeholder) {
                          (placeholder as HTMLElement).style.display = 'flex';
                        }
                      }
                    }}
                  />
                ) : null}
                
                {/* Placeholder icon for missing images */}
                <div 
                  className="placeholder-icon absolute inset-0 flex items-center justify-center bg-gray-100"
                  style={{ display: imageUrl ? 'none' : 'flex' }}
                >
                  <Image className="w-12 h-12 text-gray-300" />
                </div>
                
                {/* Gender Badge */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-primary text-white font-black">
                    {event.gender === "Men" ? "BOYS" : event.gender === "Women" ? "GIRLS" : "MIXED"}
                  </Badge>
                </div>
                
                {/* Registration Open Badge */}
                {isRegistrationOpen(event.registration_open_date, event.registration_close_date) && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-secondary text-slate-900 font-black">
                      REGISTRATION OPEN
                    </Badge>
                  </div>
                )}
              </div>
              
              <CardContent className="p-6">
                <h3 className="text-xl font-black text-slate-900 mb-4 group-hover:text-primary transition-colors line-clamp-2">
                  {event.name}
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2 text-secondary flex-shrink-0" />
                    {formatEventDate(event.start_date, event.end_date)}
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-2 text-secondary flex-shrink-0" />
                    {formatLocation(event.venue_city, event.venue_state)}
                  </div>
                </div>

                <Button 
                  className="w-full bg-secondary hover:bg-secondary/90 text-black font-black"
                  onClick={() => window.open(`/events/${event.slug || event.id}`, '_blank')}
                >
                  VIEW DETAILS
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* View More Events Button */}
      <Button 
        className="mt-8 bg-primary hover:bg-primary/90 text-white font-black px-8"
        onClick={() => window.location.href = '/events'}
      >
        VIEW MORE EVENTS
      </Button>
    </div>
  );
}
